import { useFormState } from '@vtex/admin-ui-form'
import { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { useShippingMethods } from 'hooks'
import { getErrorResponseCode } from 'shared'
import { ShippingMethods as ShippingMethodsType } from 'typings'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from '../../../Drawer'
import { NoPermissionView } from '../shared/NoPermissionView'
import { ShippingMethodsSelector } from './ShippingMethodsSelector'
import { messages } from './messages'

type ShippingMethodsProps = {
  state: UseDrawerStateReturn
  config: ShippingMethodsType | undefined
  onSave: (config: ShippingMethodsType) => void
}

export const DRAWER_FORM_NAME = 'shippingMethods'

type DrawerFormType = {
  [DRAWER_FORM_NAME]: ShippingMethodsType
}

export function ShippingMethodsEditor(props: ShippingMethodsProps) {
  const { state: drawerState, onSave, config } = props
  const { hide } = drawerState

  const { formatMessage: i18n } = useIntl()

  const isCreating = !config || config.length === 0

  const defaultValues = useMemo<DrawerFormType>(
    () => ({
      [DRAWER_FORM_NAME]: config ? [...config] : [],
    }),
    [config]
  )

  const drawerForm = useFormState<DrawerFormType>({
    defaultValues: defaultValues,
  })

  useEffect(() => {
    drawerState.open
      ? drawerForm.setValue(DRAWER_FORM_NAME, config ?? [])
      : drawerForm.reset()
  }, [drawerState.open, config, drawerForm])

  const handleSave = () => {
    onSave(drawerForm.getValues(DRAWER_FORM_NAME))
    hide()
  }

  const {
    data: shippingMethodOptions,
    isLoading: loading,
    error,
  } = useShippingMethods({ enabled: drawerState.open })

  if (getErrorResponseCode(error) === 403) {
    return (
      <NoPermissionView
        title={messages.title}
        drawerState={drawerState}
        description={messages.noPermission}
      />
    )
  }

  return (
    <Drawer state={drawerState}>
      <DrawerHeader>
        <DrawerTitle>{i18n(messages.title)}</DrawerTitle>
        <DrawerSubtitle>{i18n(messages.subtitle)}</DrawerSubtitle>
        <DismissButton onHide={hide} />
      </DrawerHeader>
      <DrawerContent>
        <ShippingMethodsSelector
          name={DRAWER_FORM_NAME}
          state={drawerForm}
          shippingOptions={shippingMethodOptions}
          loading={loading}
        />
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton isCreating={isCreating} onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
